// src/pages/TrainingGuide.js
import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getProgramBySlug } from '../data/trainingData';
import 'highlight.js/styles/github.css';
import '../styles/TrainingGuide.css';

const TrainingGuide = () => {
  const { slug } = useParams();
  const program = getProgramBySlug(slug);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('loading'); // loading | success | error

  useEffect(() => {
    if (!program || !program.trainingGuideUrl) return;
    setStatus('loading');
    fetch(program.trainingGuideUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Guide not found');
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setStatus('success');
      })
      .catch(() => setStatus('error'));
  }, [program]);

  if (!program) {
    return <Navigate to="/training" replace />;
  }

  if (!program.trainingGuideUrl) {
    return <Navigate to={`/training/${program.slug}`} replace />;
  }

  return (
    <div className="training-guide-page">
      <SEO
        title={`${program.title} — Training Guide | EDSY Tech`}
        description={`Full training guide for ${program.title}: lesson-by-lesson explanations, code examples, and exercises. Read online or download as PDF.`}
        canonicalPath={`/training/${program.slug}/guide`}
        keywords={program.seo.keywords}
      />
      <Navbar />

      <section className="tg-hero">
        <div className="tg-hero-container">
          <Link to={`/training/${program.slug}`} className="tg-breadcrumb">
            <ArrowLeft className="tg-breadcrumb-icon" />
            Back to {program.title}
          </Link>
          <h1 className="tg-hero-title">{program.title} — Training Guide</h1>
          <p className="tg-hero-subtitle">
            Read the full lesson-by-lesson guide online, or download it as a PDF to keep offline.
          </p>
          {program.trainingGuidePdfUrl && (
            <a
              href={program.trainingGuidePdfUrl}
              className="tg-download-button"
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              <Download className="tg-download-icon" />
              Download PDF
            </a>
          )}
        </div>
      </section>

      <section className="tg-body">
        <div className="tg-body-container">
          {status === 'loading' && (
            <div className="tg-loading">
              <Loader2 className="tg-loading-icon" />
              Loading guide...
            </div>
          )}
          {status === 'error' && (
            <div className="tg-error">
              Sorry, we couldn't load this guide right now. Try the{' '}
              {program.trainingGuidePdfUrl ? <a href={program.trainingGuidePdfUrl}>PDF download</a> : 'link above'} instead.
            </div>
          )}
          {status === 'success' && (
            <article className="tg-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {content}
              </ReactMarkdown>
            </article>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrainingGuide;